import React, { useState } from 'react';
import { IonContent, IonPage, IonSpinner, IonAlert } from '@ionic/react';
import { Link, useHistory } from 'react-router-dom';
import { auth, db, googleProvider } from '../firebaseConfig';
import { createUserWithEmailAndPassword, sendEmailVerification, signInWithPopup } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { IonIcon } from '@ionic/react';
import { mailOutline, lockClosedOutline, personOutline, callOutline } from 'ionicons/icons';

import logo from "../assets/logo.png";

const Register: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    if (password !== confirmPassword) {
      setAlertMessage(t('register.passwordMismatch'));
      setShowAlert(true);
      return;
    }

    setIsLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      await sendEmailVerification(user);
      await setDoc(doc(db, 'users', user.uid), {
        email,
        firstName,
        lastName,
        phoneNumber,
        createdAt: new Date().toISOString(),
        subscriptionStatus: 'inactive',
        hasUnlimitedTokens: false,
        tokens: 10000,
        subscriptionPlan: null,
        subscriptionEndDate: null
      });
      history.push('/email-verification');
    } catch (error: any) {
      console.error('Registration error:', error);
      setAlertMessage(error.message);
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        firstName: user.displayName?.split(' ')[0] || '',
        lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
        phoneNumber: user.phoneNumber || '',
        createdAt: new Date().toISOString(),
        subscriptionStatus: 'inactive',
        hasUnlimitedTokens: false,
        tokens: 1000,
        subscriptionPlan: null,
        subscriptionEndDate: null
      });
      
      history.push('/teacher-selection');
    } catch (error: any) {
      console.error('Google sign-up error:', error);
      setAlertMessage(error.message);
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IonPage>
      <IonContent className="ion-padding bg-white">
        <div className="min-h-screen flex flex-col items-center justify-center px-4">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <img src={logo} alt="Logo" className="mx-auto w-32 h-32 rounded-2xl shadow-lg transform hover:scale-105 transition-transform duration-300" />
              <h1 className="text-3xl font-bold mt-6 text-white">{t('register.title')}</h1>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <button
                onClick={handleGoogleSignUp}
                disabled={isLoading}
                className="w-full bg-white border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 transition-colors duration-300 flex items-center justify-center space-x-2 font-medium"
              >
                {isLoading ? (
                  <IonSpinner name="crescent" />
                ) : (
                  <>
                    <img
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt="Google"
                      className="w-5 h-5"
                    />
                    <span>{t('register.continueWithGoogle')}</span>
                  </>
                )}
              </button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              <form onSubmit={handleRegister} className="space-y-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <IonIcon icon={mailOutline} className="text-gray-400 w-5 h-5" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('register.email')}
                    disabled={isLoading}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-800"
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <IonIcon icon={personOutline} className="text-gray-400 w-5 h-5" />
                  </div>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t('register.firstName')}
                    disabled={isLoading}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-800"
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <IonIcon icon={personOutline} className="text-gray-400 w-5 h-5" />
                  </div>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t('register.lastName')}
                    disabled={isLoading}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-800"
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <IonIcon icon={callOutline} className="text-gray-400 w-5 h-5" />
                  </div>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder={t('register.phoneNumber')}
                    disabled={isLoading}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-800"
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <IonIcon icon={lockClosedOutline} className="text-gray-400 w-5 h-5" />
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('register.password')}
                    disabled={isLoading}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-800"
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <IonIcon icon={lockClosedOutline} className="text-gray-400 w-5 h-5" />
                  </div>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('register.confirmPassword')}
                    disabled={isLoading}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-800"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#31D526] text-white py-3 rounded-lg hover:bg-[#28b820] transition-colors duration-300 flex items-center justify-center font-medium text-lg"
                >
                  {isLoading ? <IonSpinner name="crescent" /> : t('register.registerButton')}
                </button>
              </form>
            </div>

            <div className="text-center">
              <p className="text-gray-600">
                {t('login.noAccount')}{' '}
                <Link 
                  to="/login" 
                  className="text-[#31D526] hover:text-[#28b820] transition-colors duration-300 font-bold text-lg"
                >
                  {t('login.loginButton')}
                </Link>
              </p>
            </div>
          </div>
        </div>

        <IonAlert
          isOpen={showAlert}
          onDidDismiss={() => setShowAlert(false)}
          header={t('common.error')}
          message={alertMessage}
          buttons={[t('common.ok')]}
        />
      </IonContent>
    </IonPage>
  );
};

export default Register;
